import { getAuthToken } from '../utils/authUtils'; //
// import { getFirebaseConfig } from '../firebase/config';

class FlowsheetService {
  constructor() {
    this.baseURL = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';
  }

  async getAuthHeaders() {
    const token = await getAuthToken();
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  // Create a new flowsheet entry
  async createFlowsheet(encounterId, flowsheetData) {
    try {
      const headers = await this.getAuthHeaders();
      // Since there's no direct flowsheet creation endpoint, we'll add it to the encounter
      // This is a workaround until a proper flowsheet creation endpoint is created
      const response = await fetch(`${this.baseURL}/internal/encounters/${encounterId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          flowsheetEntry: {
            ...flowsheetData,
            time: new Date().toISOString(),
            createdAt: new Date().toISOString()
          }
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to create flowsheet: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating flowsheet:', error);
      throw error;
    }
  }

  // Update an existing flowsheet entry
  async updateFlowsheet(encounterId, flowsheetId, flowsheetData) {
    try {
      const headers = await this.getAuthHeaders();
      // Since there's no direct flowsheet update endpoint, we'll update the encounter
      // This is a workaround until a proper flowsheet update endpoint is created
      const response = await fetch(`${this.baseURL}/internal/encounters/${encounterId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          flowsheetUpdate: {
            flowsheetId,
            ...flowsheetData,
            updatedAt: new Date().toISOString()
          }
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to update flowsheet: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating flowsheet:', error);
      throw error;
    }
  }

  // Get flowsheet entries for an encounter
  async getFlowsheetEntries(encounterId) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${this.baseURL}/internal/encounters/${encounterId}/flowsheets`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error(`Failed to get flowsheet entries: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting flowsheet entries:', error);
      throw error;
    }
  }

  // Get all flowsheet entries for inpatient encounters
  async getInpatientFlowsheets() {
    try {
      const headers = await this.getAuthHeaders();
      // Get inpatient encounters first, then get flowsheets for each
      const encountersResponse = await fetch(`${this.baseURL}/internal/encounters?type=inpatient&status=active,in-progress,admitted`, {
        method: 'GET',
        headers
      });

      if (!encountersResponse.ok) {
        throw new Error(`Failed to get inpatient encounters: ${encountersResponse.statusText}`);
      }

      const encountersData = await encountersResponse.json();
      const encounters = encountersData.encounters || [];
      
      // Get flowsheets for each encounter
      const allFlowsheets = [];
      for (const encounter of encounters) {
        try {
          const flowsheetResponse = await fetch(`${this.baseURL}/internal/encounters/${encounter.id}/flowsheets`, {
            method: 'GET',
            headers
          });
          
          if (flowsheetResponse.ok) {
            const flowsheetData = await flowsheetResponse.json();
            allFlowsheets.push(...(flowsheetData.flowsheets || []));
          }
        } catch (error) {
          console.warn(`Failed to get flowsheets for encounter ${encounter.id}:`, error);
        }
      }

      return { flowsheets: allFlowsheets };
    } catch (error) {
      console.error('Error getting inpatient flowsheets:', error);
      throw error;
    }
  }

  // Get flowsheet entries with patient and encounter data
  async getFlowsheetsWithPatientData() {
    try {
      const headers = await this.getAuthHeaders();
      // Get all active encounters first
      const encountersResponse = await fetch(`${this.baseURL}/internal/encounters?status=active,in-progress,admitted`, {
        method: 'GET',
        headers
      });

      if (!encountersResponse.ok) {
        throw new Error(`Failed to get encounters: ${encountersResponse.statusText}`);
      }

      const encountersData = await encountersResponse.json();
      const encounters = encountersData.encounters || [];
      
      // Get flowsheets for each encounter and enrich with patient/encounter data
      const enrichedFlowsheets = [];
      for (const encounter of encounters) {
        try {
          const flowsheetResponse = await fetch(`${this.baseURL}/internal/encounters/${encounter.id}/flowsheets`, {
            method: 'GET',
            headers
          });
          
          if (flowsheetResponse.ok) {
            const flowsheetData = await flowsheetResponse.json();
            const flowsheets = flowsheetData.flowsheets || [];
            
            // Enrich each flowsheet with encounter and patient data
            const enriched = flowsheets.map(flowsheet => ({
              ...flowsheet,
              encounter: {
                id: encounter.id,
                status: encounter.status,
                type: encounter.type,
                date: encounter.date,
                facilityId: encounter.facilityId
              },
              patient: {
                id: encounter.patientId,
                name: encounter.patientName,
                mrn: encounter.mrn
              }
            }));
            
            enrichedFlowsheets.push(...enriched);
          }
        } catch (error) {
          console.warn(`Failed to get flowsheets for encounter ${encounter.id}:`, error);
        }
      }

      return { flowsheets: enrichedFlowsheets };
    } catch (error) {
      console.error('Error getting flowsheets with patient data:', error);
      throw error;
    }
  }
}

export default FlowsheetService;
